import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Search, User, Mail, Building, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI } from '../services/api';
import { User as UserType } from '../types';
import logoImage from '../images/image.png';

type UserFilter = 'all' | 'active' | 'inactive';

const ActiveUsersDashboard: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<UserType[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [userFilter, setUserFilter] = useState<UserFilter>('all');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const usersData = await usersAPI.getAllUsers();
      setUsers(usersData);
      setFilteredUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    filterUsers();
  }, [searchTerm, users, userFilter]);

  const filterUsers = () => {
    let filtered = [...users];

    // Filter by user status (active/inactive/all)
    if (userFilter === 'active') {
      filtered = filtered.filter(u => {
        const isActive: any = u.isActive !== undefined ? u.isActive : u.is_active;
        if (isActive === true || isActive === 1) return true;
        if (isActive === false || isActive === 0) return false;
        return isActive === null || isActive === undefined;
      });
    } else if (userFilter === 'inactive') {
      filtered = filtered.filter(u => {
        const isActive: any = u.isActive !== undefined ? u.isActive : u.is_active;
        return isActive === false || isActive === 0;
      });
    }
    // If userFilter === 'all', show all users

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(u =>
        (u.firstName || u.first_name)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (u.lastName || u.last_name)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.institution?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredUsers(filtered);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'superadmin': return 'bg-red-100 text-red-800 border-red-200';
      case 'admin': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'user': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRoleText = (role: string) => {
    switch (role) {
      case 'superadmin': return 'Super Admin';
      case 'admin': return 'Admin';
      case 'user': return 'User';
      default: return 'Unknown';
    }
  };

  // Active users: is_active = 1 OR is_active IS NULL (matches SQL query logic)
  const activeUsers = users.filter(u => {
    const isActive: any = u.isActive !== undefined ? u.isActive : u.is_active;
    // Handle both boolean and number types from database
    // MySQL returns 1/0 as numbers, but TypeScript types it as boolean
    if (isActive === true || isActive === 1) return true;
    if (isActive === false || isActive === 0) return false;
    // null or undefined means active (default behavior)
    return isActive === null || isActive === undefined;
  });
  // Inactive users: is_active = 0 or false
  const inactiveUsers = users.filter(u => {
    const isActive: any = u.isActive !== undefined ? u.isActive : u.is_active;
    // Handle both boolean and number types from database
    return isActive === false || isActive === 0;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <img 
            src={logoImage} 
            alt="Publication Tracker Logo" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading active users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Link 
                to="/dashboard" 
                className="mr-4 p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all duration-200"
              >
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <h1 className="text-4xl font-bold">Active Users</h1>
            </div>
            <p className="text-xl text-green-100 mb-8">
              Manage and view all system users
            </p>
            
            {/* Statistics - Clickable Filter Buttons */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              <button
                onClick={() => setUserFilter('active')}
                className={`bg-white backdrop-blur-sm rounded-lg p-4 transition-all duration-200 hover:bg-opacity-30 ${
                  userFilter === 'active' 
                    ? 'bg-opacity-30 ring-2 ring-white ring-opacity-50 shadow-lg transform scale-105' 
                    : 'bg-opacity-20 hover:scale-105'
                }`}
              >
                <Users className="h-8 w-8 mx-auto mb-2" />
                <div className="text-2xl font-bold">{activeUsers.length}</div>
                <div className="text-sm text-green-100">Active Users</div>
              </button>
              <button
                onClick={() => setUserFilter('inactive')}
                className={`bg-white backdrop-blur-sm rounded-lg p-4 transition-all duration-200 hover:bg-opacity-30 ${
                  userFilter === 'inactive' 
                    ? 'bg-opacity-30 ring-2 ring-white ring-opacity-50 shadow-lg transform scale-105' 
                    : 'bg-opacity-20 hover:scale-105'
                }`}
              >
                <User className="h-8 w-8 mx-auto mb-2" />
                <div className="text-2xl font-bold">{inactiveUsers.length}</div>
                <div className="text-sm text-green-100">Inactive Users</div>
              </button>
              <button
                onClick={() => setUserFilter('all')}
                className={`bg-white backdrop-blur-sm rounded-lg p-4 transition-all duration-200 hover:bg-opacity-30 ${
                  userFilter === 'all' 
                    ? 'bg-opacity-30 ring-2 ring-white ring-opacity-50 shadow-lg transform scale-105' 
                    : 'bg-opacity-20 hover:scale-105'
                }`}
              >
                <Building className="h-8 w-8 mx-auto mb-2" />
                <div className="text-2xl font-bold">{users.length}</div>
                <div className="text-sm text-green-100">Total Users</div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <Users className="h-6 w-6 mr-2 text-green-600" />
              User Directory
            </h2>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent w-64"
              />
            </div>
          </div>
        </div>

        {/* Users Display */}
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm ? 'No users found' : 'No users available'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm 
                ? 'Try adjusting your search terms' 
                : 'Users will appear here once they are registered'
              }
            </p>
            {!searchTerm && (
              <Link
                to="/publications"
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
            )}
          </div>
        ) : (
          <>
            {/* Results Count */}
            <div className="mb-6">
              <p className="text-gray-600">
                {userFilter === 'all' && `Showing ${filteredUsers.length} of ${users.length} users`}
                {userFilter === 'active' && `Showing ${filteredUsers.length} active user${filteredUsers.length !== 1 ? 's' : ''}`}
                {userFilter === 'inactive' && `Showing ${filteredUsers.length} inactive user${filteredUsers.length !== 1 ? 's' : ''}`}
                {searchTerm && ` (filtered by "${searchTerm}")`}
              </p>
            </div>

            {/* Users Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredUsers.map((userItem) => (
                <div key={userItem.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <User className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {userItem.firstName || userItem.first_name} {userItem.lastName || userItem.last_name}
                        </h3>
                        <p className="text-sm text-gray-600">{userItem.email}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRoleColor(userItem.role)}`}>
                      {getRoleText(userItem.role)}
                    </span>
                  </div>

                  <div className="space-y-2">
                    {userItem.institution && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Building className="h-4 w-4 mr-2" />
                        <span>{userItem.institution}</span>
                      </div>
                    )}
                    {userItem.department && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Building className="h-4 w-4 mr-2" />
                        <span>{userItem.department}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${
                        (userItem.isActive !== false && userItem.is_active !== false) ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {(userItem.isActive !== false && userItem.is_active !== false) ? 'Active' : 'Inactive'}
                      </span>
                      {(userItem.isActive === false || userItem.is_active === false) && (
                        <span className="text-xs text-red-500 bg-red-50 px-2 py-1 rounded">
                          Deactivated
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ActiveUsersDashboard;
